import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ComponentProps, useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { ShoppingCart, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface CartDrawerProps {
  triggerClassName?: string;
  triggerVariant?: ComponentProps<typeof Button>["variant"];
  triggerSize?: ComponentProps<typeof Button>["size"];
  hideLabel?: boolean;
  triggerAriaLabel?: string;
}

export function CartDrawer({
  triggerClassName,
  triggerVariant = "outline",
  triggerSize = "default",
  hideLabel = false,
  triggerAriaLabel = "Open cart",
}: CartDrawerProps = {}) {
  const { items, total, remove, update, clear } = useCart();
  const totalQty = items.reduce((sum,i)=>sum+i.qty,0);
  const btnRef = useRef<HTMLButtonElement|null>(null);
  useEffect(()=>{
    const el = btnRef.current;
    if (!el) return;
    el.classList.add('bump');
    const t = setTimeout(()=>el.classList.remove('bump'), 300);
    return ()=>clearTimeout(t);
  }, [totalQty]);

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          ref={btnRef}
          variant={triggerVariant}
          size={triggerSize}
          className={cn("relative group", triggerClassName)}
          aria-label={triggerAriaLabel}
        >
          <ShoppingCart
            className={cn(
              "transition-transform group-[.bump]:-translate-y-0.5",
              hideLabel ? "" : "mr-2"
            )}
          />
          {hideLabel ? null : "Cart"}
          {totalQty > 0 ? (
            <span className="absolute -top-2 -right-2 bg-primary text-white rounded-full text-xs min-w-5 h-5 px-1 flex items-center justify-center animate-in fade-in zoom-in-50" aria-live="polite">
              {totalQty}
            </span>
          ) : null}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Your Cart</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground">Your cart is empty.</div>
          ) : (
            items.map((item) => (
              <div key={item.sku} className="flex items-center justify-between border-b pb-3">
                <div>
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm text-muted-foreground">${item.price.toFixed(2)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={item.qty}
                    onChange={(e) => update(item.sku, Number(e.target.value) || 1)}
                    className="w-16 border rounded px-2 py-1 text-right"
                  />
                  <Button variant="ghost" size="icon" onClick={() => remove(item.sku)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <SheetFooter className="mt-6">
          <div className="w-full space-y-4">
            <div className="flex items-center justify-between text-lg font-semibold">
              <div>Total</div>
              <div>${total.toFixed(2)}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={clear}>Clear</Button>
              <Button className="flex-1" onClick={() => { window.location.href = '/checkout'; }}>Checkout</Button>
            </div>
          </div>
        </SheetFooter>
        <SheetClose />
      </SheetContent>
    </Sheet>
  );
}
